// Central dispatcher — replaces the 2000-line if/else chain in handleFixMessage.
// Each handler returns true if it consumed the message, false to pass to the next.

import type { MessageCallbacks } from './types';
import { handleOrderBook } from './orderBookHandler';
import { handleAnalytics } from './analyticsHandler';
import { handleSignals } from './signalsHandler';
import { handleMlPrediction, handleRecentTrades } from './mlPredictionHandler';
import {
  handleQuoteSuggestion, handleInventoryUpdate, handleSettlementUpdate,
  handlePerformanceUpdate, handleModelHealth, handleRiskLimit,
  handleActivityEvent, handleRegimeUpdate, handleSprintUpdate,
  handleModelUpdate, handleDailySummary, handleMonthlySummary,
} from './engineStateHandlers';
import { handleMarketStats, handleTwap, handleVwamp, handleMicroprice } from './chartDataHandlers';
import { handleBookChanges } from './bookChangesHandler';
import { handleOrderAck, handleOrderReject, handleExecutionReport, handleOrderCancel } from './orderHandlers';
import { handlePositionUpdate, handlePosition, handlePortfolioTotals } from './positionHandlers';
import { handleHeartbeat, handleFixSessionEvent, handleConfigAck } from './systemHandlers';
import { handleAlert, handleCommandAck } from './controlHandlers';

export type { MessageCallbacks } from './types';
export { normalizeTimestamp } from './types';

export { handleOrderBook } from './orderBookHandler';
export { handleAnalytics, normalizeAnalytics } from './analyticsHandler';
export { handleSignals } from './signalsHandler';
export { handleMlPrediction, handleRecentTrades } from './mlPredictionHandler';
export {
  handleQuoteSuggestion, handleInventoryUpdate, handleSettlementUpdate,
  handlePerformanceUpdate, handleModelHealth, handleRiskLimit,
  handleActivityEvent, handleRegimeUpdate, handleSprintUpdate,
  handleModelUpdate, handleDailySummary, handleMonthlySummary,
} from './engineStateHandlers';
export { handleMarketStats, handleTwap, handleVwamp, handleMicroprice } from './chartDataHandlers';
export { handleBookChanges } from './bookChangesHandler';
export { handleOrderAck, handleOrderReject, handleExecutionReport, handleOrderCancel } from './orderHandlers';
export { handlePositionUpdate, handlePosition, handlePortfolioTotals } from './positionHandlers';
export { handleHeartbeat, handleFixSessionEvent, handleConfigAck } from './systemHandlers';
export { handleAlert, handleCommandAck } from './controlHandlers';

type Handler = (obj: any, cb: MessageCallbacks) => boolean;

// Ordered list of JSON message handlers. First match wins.
const HANDLERS: Handler[] = [
  // Market data
  handleOrderBook,
  handleAnalytics,
  handleSignals,
  handleMlPrediction,
  handleRecentTrades,
  // Chart data
  handleMarketStats,
  handleTwap,
  handleVwamp,
  handleMicroprice,
  // Book
  handleBookChanges,
  // Engine state
  handleQuoteSuggestion,
  handleInventoryUpdate,
  handleSettlementUpdate,
  handlePerformanceUpdate,
  handleModelHealth,
  handleRiskLimit,
  handleActivityEvent,
  handleRegimeUpdate,
  handleSprintUpdate,
  handleModelUpdate,
  handleDailySummary,
  handleMonthlySummary,
  // Orders
  handleOrderAck,
  handleOrderReject,
  handleOrderCancel,
  handleExecutionReport,
  // Positions
  handlePositionUpdate,
  handlePosition,
  handlePortfolioTotals,
  // System
  handleHeartbeat,
  handleFixSessionEvent,
  handleConfigAck,
  // Control plane
  handleAlert,
  handleCommandAck,
];

/**
 * Dispatch a parsed JSON message to the appropriate handler.
 * Returns true if a handler consumed the message, false if unrecognized.
 */
export function dispatchMessage(obj: any, cb: MessageCallbacks): boolean {
  for (const handler of HANDLERS) {
    if (handler(obj, cb)) return true;
  }
  return false;
}
